import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { SubTradeRequest } from '../../../../interfaces/subTradeRequest';
import { Badge } from '../../../2-component';
import { formatMoneyValue } from '../../../../util';
import { useLocalization } from '../../../../util/useLocalization';
import { useThemeTokens } from '../../../../providers/themeTokenProvider';
import { Link } from '../../../1-primative';

type ColumnData = { data: SubTradeRequest };

export const AccountColumn = ({ data }: ColumnData): ReactElement => {
  const { t } = useTranslation(['components', 'shared', 'transfer']);

  if (!data.subAccount?.account?.user || !data.subAccount?.account?.type) return <></>;

  const isEntity = !!data.subAccount?.account.user.entityName;
  const userName = data.subAccount?.account?.user?.firstName ?? data.subAccount?.account?.user?.entityName;
  const basePath = isEntity ? '/nonIndividualClients' : '/clients';

  return (
    <Link
      href={`${basePath}/${data.subAccount?.account?.user?.id}/account/${data.subAccount?.account?.id}`}
      target='_blank'
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
      }}
    >
      {userName}'s {t(`accountTypes:${data.subAccount?.account.type}`)}
    </Link>
  );
};

export const PortfolioColumn = ({ data }: ColumnData): ReactElement => (<>Core - Medium Risk</>);

export const ClientColumn = ({ data }: ColumnData): ReactElement => (<>{data.user?.firstName} {data.user?.lastName}</>);

export const SecurityColumn = ({ data }: ColumnData): ReactElement => (<>{ data.financialProduct?.ticker || '-' }</>);

export const TradeColumn = ({ data }: ColumnData): ReactElement => {
  const theme = useThemeTokens();

  return (<div style={{ color: data.type === 'BUY' ? theme.ref.palette.positive60 : theme.ref.palette.negative60 }}>
    { formatMoneyValue(data.expectedTradePriceCents || 0) }
  </div>);
};

export const StatusColumn = ({ data }: ColumnData): ReactElement => {
  const colorByState: any = {
    INITIATED: 'warning',
    READY: 'warning',
    CANCELED: 'negative',
    RECONCILED: 'positive',
    REQUESTED: 'neutral',
  };

  return (<Badge label={data.state} color={colorByState[data.state]} />);
};

export const TradeDateColumn = ({ data }: ColumnData): ReactElement => {
  const { localizedDateTime } = useLocalization();
  return (<>{data.updatedAt ? localizedDateTime(data.updatedAt, false, 'DD-MMM-YYYY') : '-'}</>);
};

export const SettlementDateColumn = ({ data }: ColumnData): ReactElement => {
  const { localizedDateTime } = useLocalization();
  return (<>{data.settlementDate ? localizedDateTime(data.settlementDate, false, 'DD-MMM-YYYY') : '-'}</>);
};

export const VerificationDateColumn = ({ data }: ColumnData): ReactElement => {
  const { localizedDateTime } = useLocalization();
  return (
    <>
      { data.verifiedByOrganizationUser && <>
        <span>{ data.verifiedByOrganizationUser.firstName }{ data.verifiedByOrganizationUser.lastName }</span> -
        <span>{ localizedDateTime(data.verifiedAt) }</span>
      </> }
      { !data.verifiedByOrganizationUser && '-' }
    </>
  );
};
